import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Layers,
  Rocket,
  Zap,
  Target,
  DollarSign,
  Key,
  CreditCard,
  ArrowRight,
  Copy,
  LogOut,
  ChevronDown,
  Settings
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SelfServeDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, refreshUser } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFundModal, setShowFundModal] = useState(false);
  const [fundAmount, setFundAmount] = useState(100);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [campaignName, setCampaignName] = useState("");
  const [budget, setBudget] = useState(100);

  useEffect(() => {
    fetchData();
    checkPaymentStatus();
  }, []);

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get("session_id");
    const paymentStatus = searchParams.get("payment");
    
    if (sessionId && paymentStatus === "success") {
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 5) return;
        try {
          const response = await axios.get(`${API}/payments/status/${sessionId}`);
          if (response.data.payment_status === "paid") {
            toast.success(`$${response.data.amount} added!`);
            await refreshUser();
            navigate("/self-serve", { replace: true });
          } else {
            setTimeout(() => pollStatus(attempts + 1), 2000);
          }
        } catch (error) {
          console.error(error);
        }
      };
      await pollStatus();
    }
  };

  const fetchData = async () => {
    try {
      const [templatesRes, campaignsRes] = await Promise.all([
        axios.get(`${API}/templates`),
        axios.get(`${API}/campaigns`)
      ]);
      setTemplates(templatesRes.data);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickStart = async () => {
    if (!selectedTemplate) {
      toast.error("Please select a campaign template");
      return;
    }
    if (!campaignName.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }
    if (user?.balance < budget) {
      toast.error("Insufficient balance. Please fund your account.");
      setShowFundModal(true);
      return;
    }

    try {
      const template = templates.find(t => t.id === selectedTemplate);
      const response = await axios.post(`${API}/campaigns`, {
        name: campaignName,
        budget: budget,
        daily_budget: budget / 30,
        start_date: new Date().toISOString().split('T')[0],
        bid_type: template.bid_type,
        bid_amount: template.suggested_bid,
        targeting: template.targeting,
        status: "active"
      });
      
      toast.success("Campaign launched! 🚀");
      setCampaigns([response.data, ...campaigns]);
      setCampaignName("");
      setSelectedTemplate(null);
      setBudget(100);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create campaign");
    }
  };

  const handleFundAccount = async () => {
    try {
      const response = await axios.post(`${API}/payments/create-checkout`, {
        amount: parseFloat(fundAmount),
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to initiate payment");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="self-serve-dashboard">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
                <Layers className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-white">MediaTNT</span>
              <span className="px-2 py-0.5 rounded bg-primary/20 text-primary text-xs font-medium">Self-Serve</span>
            </Link>

            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg bg-night-paper border border-night-subtle">
                <DollarSign className="w-4 h-4 text-emerald" />
                <span className="font-mono text-white">${user?.balance?.toFixed(2) || "0.00"}</span>
              </div>

              <Link to="/dashboard">
                <Button variant="outline" className="border-night-subtle" data-testid="full-dashboard-btn">
                  Full Dashboard
                </Button>
              </Link>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-night-paper transition-colors">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-night-paper border-night-subtle">
                  <DropdownMenuItem className="text-muted-foreground">
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-rose" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-5xl mx-auto">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Launch Your Campaign in Minutes
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Choose a template, set your budget, and start reaching your audience. 
            No experience required.
          </p>
        </motion.div>

        {/* Quick Launch Section */}
        <div className="rounded-2xl bg-night-paper border border-night-subtle p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Rocket className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-semibold text-white">Quick Launch</h2>
              <p className="text-sm text-muted-foreground">Get your ads live in 3 simple steps</p>
            </div>
          </div>

          {/* Step 1: Choose Template */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-xs font-bold text-white">1</span>
              <span className="font-semibold text-white">Choose a Campaign Template</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    selectedTemplate === template.id
                      ? "border-primary bg-primary/10"
                      : "border-night-subtle bg-night-bg hover:border-primary/50"
                  }`}
                  data-testid={`template-${template.id}`}
                >
                  <div className="font-semibold text-white mb-1">{template.name}</div>
                  <div className="text-xs text-muted-foreground mb-3">{template.description}</div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-primary/20 text-primary text-xs">
                      {template.bid_type.toUpperCase()}
                    </span>
                    <span className="text-xs text-muted-foreground">${template.suggested_bid}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Step 2: Name & Budget */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-xs font-bold text-white">2</span>
              <span className="font-semibold text-white">Set Name & Budget</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Campaign Name</Label>
                <Input
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="My First Campaign"
                  className="bg-night-bg border-night-subtle"
                  data-testid="campaign-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Budget ($)</Label>
                <Input
                  type="number"
                  value={budget}
                  onChange={(e) => setBudget(parseFloat(e.target.value))}
                  min="50"
                  step="10"
                  className="bg-night-bg border-night-subtle"
                  data-testid="budget-input"
                />
              </div>
            </div>
          </div>

          {/* Step 3: Launch */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-xs font-bold text-white">3</span>
              <span className="font-semibold text-white">Launch Your Campaign</span>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Button 
                onClick={handleQuickStart}
                className="w-full sm:w-auto bg-primary hover:bg-indigo-hover btn-glow px-8"
                disabled={!selectedTemplate || !campaignName}
                data-testid="launch-campaign-btn"
              >
                <Rocket className="w-4 h-4 mr-2" />
                Launch Campaign
              </Button>
              {user?.balance < budget && (
                <Button 
                  onClick={() => setShowFundModal(true)}
                  variant="outline"
                  className="w-full sm:w-auto border-emerald text-emerald hover:bg-emerald/10"
                  data-testid="fund-first-btn"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Fund Account First
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* API Key Section */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-lg bg-night-paper border border-night-subtle p-6">
            <div className="flex items-center gap-3 mb-4">
              <Key className="w-5 h-5 text-primary" />
              <h3 className="font-heading font-semibold text-white">Your API Key</h3>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 p-3 rounded bg-night-bg border border-night-subtle font-mono text-xs text-muted-foreground truncate">
                {user?.api_key || "No API key"}
              </div>
              <Button 
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(user?.api_key || "");
                  toast.success("Copied!");
                }}
                className="border-night-subtle"
                data-testid="copy-api-key-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Use this key for custom API integrations.
            </p>
          </div>

          <div className="rounded-lg bg-night-paper border border-night-subtle p-6">
            <div className="flex items-center gap-3 mb-4">
              <DollarSign className="w-5 h-5 text-emerald" />
              <h3 className="font-heading font-semibold text-white">Account Balance</h3>
            </div>
            <div className="font-heading text-3xl font-bold text-white mb-4">
              ${user?.balance?.toFixed(2) || "0.00"}
            </div>
            <Button 
              onClick={() => setShowFundModal(true)}
              className="w-full bg-emerald hover:bg-emerald/90"
              data-testid="add-funds-btn"
            >
              Add Funds
            </Button>
          </div>
        </div>

        {/* Recent Campaigns */}
        {campaigns.length > 0 && (
          <div className="mt-8 rounded-lg bg-night-paper border border-night-subtle overflow-hidden">
            <div className="p-6 border-b border-night-subtle">
              <h3 className="font-heading font-semibold text-white">Your Campaigns</h3>
            </div>
            <div className="divide-y divide-night-subtle">
              {campaigns.slice(0, 5).map((campaign) => (
                <div key={campaign.id} className="p-4 flex items-center justify-between" data-testid={`campaign-${campaign.id}`}>
                  <div>
                    <div className="font-semibold text-white">{campaign.name}</div>
                    <div className="text-sm text-muted-foreground">
                      ${campaign.budget} budget · {campaign.impressions?.toLocaleString() || 0} impressions
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    campaign.status === "active" ? "bg-emerald/20 text-emerald" :
                    campaign.status === "paused" ? "bg-yellow-500/20 text-yellow-500" :
                    "bg-night-subtle text-muted-foreground"
                  }`}>
                    {campaign.status}
                  </span>
                </div>
              ))}
            </div>
            {campaigns.length > 5 && (
              <Link to="/dashboard" className="block p-4 text-center text-primary hover:bg-night-subtle/50 transition-colors">
                View all campaigns →
              </Link>
            )}
          </div>
        )}
      </main>

      {/* Fund Modal */}
      <Dialog open={showFundModal} onOpenChange={setShowFundModal}>
        <DialogContent className="bg-night-paper border-night-subtle">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">Fund Your Account</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="p-4 rounded-lg bg-night-bg border border-night-subtle">
              <div className="text-sm text-muted-foreground mb-1">Current Balance</div>
              <div className="font-heading text-3xl font-bold text-white">
                ${user?.balance?.toFixed(2) || "0.00"}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Select Amount</Label>
              <div className="grid grid-cols-4 gap-2">
                {[50, 100, 250, 500].map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => setFundAmount(amount)}
                    className={`p-3 rounded-lg border font-semibold transition-colors ${
                      fundAmount === amount
                        ? "border-primary bg-primary/20 text-white"
                        : "border-night-subtle bg-night-bg text-muted-foreground hover:border-primary/50"
                    }`}
                    data-testid={`fund-${amount}`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={handleFundAccount}
              className="w-full bg-emerald hover:bg-emerald/90"
              data-testid="pay-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Pay ${fundAmount.toFixed(2)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SelfServeDashboard;
